/**
 * ArtifactContainer - UI wrapper com abas PREVIEW | CODE e ações.
 *
 * Novidades em relação à versão anterior:
 *   - Loading skeleton enquanto o sandbox carrega (via onReady callback)
 *   - setSandbox aceita callback onReady do ArtifactSandbox
 */

import { ArtifactSandbox } from './ArtifactSandbox';

export type ArtifactTab = 'preview' | 'code';

export interface ArtifactContainerOptions {
    type: string;
    source: string;
    renderToHtml: (source: string) => string;
    minHeight?: number;
    maxHeight?: number;
    seamless?: boolean;
}

const TYPE_META: Record<string, { icon: string; label: string }> = {
    react:   { icon: '⚛️', label: 'React Component' },
    html:    { icon: '🌐', label: 'HTML Preview' },
    mermaid: { icon: '📊', label: 'Diagram' },
    json:    { icon: '📋', label: 'JSON Viewer' },
    python:  { icon: '🐍', label: 'Python' },
};

export class ArtifactContainer {
    private container: HTMLDivElement;
    private previewPanel: HTMLDivElement;
    private codePanel: HTMLDivElement;
    private codeEditor: HTMLTextAreaElement;
    private loadingEl: HTMLDivElement | null = null;
    private sandbox: ArtifactSandbox | null = null;
    private activeTab: ArtifactTab = 'preview';
    private currentSource: string;
    private originalSource: string;
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(private options: ArtifactContainerOptions) {
        this.currentSource  = options.source;
        this.originalSource = options.source;
        this.container      = this.buildContainer();
        this.previewPanel   = this.container.querySelector('.artifact-panel-preview') as HTMLDivElement;
        this.codePanel      = this.container.querySelector('.artifact-panel-code') as HTMLDivElement;
        this.codeEditor     = this.container.querySelector('.artifact-code-editor') as HTMLTextAreaElement;
    }

    // -------------------------------------------------------------------------
    // DOM
    // -------------------------------------------------------------------------

    private buildContainer(): HTMLDivElement {
        const meta = TYPE_META[this.options.type] ?? { icon: '📦', label: 'Artifact' };

        const el = document.createElement('div');
        el.className = 'artifact-container';
        if (this.options.seamless) el.classList.add('artifact-container--seamless');
        el.dataset.artifactType = this.options.type;

        const headerHtml = this.options.seamless ? '' : `
            <div class="artifact-header">
                <div class="artifact-header-left">
                    <span class="artifact-icon">${meta.icon}</span>
                    <span class="artifact-label">${meta.label}</span>
                </div>
                <div class="artifact-header-right">
                    <div class="artifact-tabs">
                        <button type="button" class="artifact-tab active" data-tab="preview">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                            Preview
                        </button>
                        <button type="button" class="artifact-tab" data-tab="code">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <polyline points="16 18 22 12 16 6"></polyline>
                                <polyline points="8 6 2 12 8 18"></polyline>
                            </svg>
                            Code
                        </button>
                    </div>
                    <div class="artifact-actions">
                        <button type="button" class="artifact-action-btn" data-action="copy" title="Copiar código">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                        </button>
                        <button type="button" class="artifact-action-btn" data-action="reset" title="Resetar para original">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <polyline points="23 4 23 10 17 10"></polyline>
                                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>`;

        el.innerHTML = `
            ${headerHtml}
            <div class="artifact-body">
                <div class="artifact-panel artifact-panel-preview active"></div>
                ${this.options.seamless ? '' : `
                <div class="artifact-panel artifact-panel-code">
                    <textarea class="artifact-code-editor" spellcheck="false" autocomplete="off" autocorrect="off" autocapitalize="off">${this.escapeHtml(this.options.source)}</textarea>
                </div>`}
            </div>`;

        // Tabs
        el.querySelectorAll<HTMLElement>('.artifact-tab').forEach(tab => {
            tab.addEventListener('click', () => this.switchTab(tab.dataset.tab as ArtifactTab));
        });

        el.querySelector('[data-action="copy"]')?.addEventListener('click', () => this.copyCode());
        el.querySelector('[data-action="reset"]')?.addEventListener('click', () => this.resetCode());

        // Editor hot-reload
        const editor = el.querySelector<HTMLTextAreaElement>('.artifact-code-editor');
        if (editor) {
            editor.addEventListener('input', () => {
                this.currentSource = editor.value;
                this.scheduleHotReload();
            });
            editor.addEventListener('keydown', (e: KeyboardEvent) => {
                if (e.key === 'Tab') {
                    e.preventDefault();
                    const s = editor.selectionStart, end = editor.selectionEnd;
                    editor.value = editor.value.substring(0, s) + '  ' + editor.value.substring(end);
                    editor.selectionStart = editor.selectionEnd = s + 2;
                    this.currentSource = editor.value;
                    this.scheduleHotReload();
                }
            });
        }

        return el;
    }

    // -------------------------------------------------------------------------
    // Tabs
    // -------------------------------------------------------------------------

    private switchTab(tab: ArtifactTab): void {
        this.activeTab = tab;
        this.container.querySelectorAll<HTMLElement>('.artifact-tab').forEach(t => {
            t.classList.toggle('active', t.dataset.tab === tab);
        });
        this.previewPanel.classList.toggle('active', tab === 'preview');
        this.codePanel?.classList.toggle('active', tab === 'code');
        if (tab === 'code') requestAnimationFrame(() => this.codeEditor?.focus());
    }

    // -------------------------------------------------------------------------
    // Hot-reload
    // -------------------------------------------------------------------------

    private scheduleHotReload(): void {
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.rerender(), 500);
    }

    private rerender(): void {
        if (!this.sandbox) return;
        try {
            this.sandbox.updateContent(this.options.renderToHtml(this.currentSource));
        } catch {
            // Mantém último estado válido
        }
    }

    // -------------------------------------------------------------------------
    // Ações
    // -------------------------------------------------------------------------

    private async copyCode(): Promise<void> {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(this.currentSource);
            } else {
                const ta = document.createElement('textarea');
                ta.value = this.currentSource;
                ta.style.cssText = 'position:fixed;left:-9999px;';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            const btn = this.container.querySelector<HTMLButtonElement>('[data-action="copy"]');
            if (btn) {
                btn.classList.add('copied');
                setTimeout(() => btn.classList.remove('copied'), 2000);
            }
        } catch { /* silently fail */ }
    }

    private resetCode(): void {
        this.currentSource = this.originalSource;
        if (this.codeEditor) this.codeEditor.value = this.originalSource;
        this.rerender();
    }

    // -------------------------------------------------------------------------
    // Loading state
    // -------------------------------------------------------------------------

    private showLoading(): void {
        const minH = this.options.minHeight ?? 200;
        const div = document.createElement('div');
        div.className = 'artifact-loading-skeleton';
        div.style.minHeight = `${minH}px`;
        // Overlay absoluto sobre o iframe (que está opacity:0 mas em fluxo normal).
        // Isso evita o espaço em branco abaixo do skeleton E permite que o iframe
        // carregue normalmente (display:none impediria o disparo do load event interno).
        div.style.position = 'absolute';
        div.style.top = '0';
        div.style.left = '0';
        div.style.right = '0';
        div.style.zIndex = '2';

        // Gera linhas de skeleton proporcionais à altura esperada do conteúdo
        const lineCount = Math.max(3, Math.floor(minH / 40));
        const widths = [90, 75, 60, 80, 50, 70, 85, 55];
        let lines = '';
        for (let i = 0; i < lineCount; i++) {
            const w = widths[i % widths.length];
            lines += `<div class="skeleton-line" style="width:${w}%"></div>`;
        }
        div.innerHTML = lines;

        this.loadingEl = div;
        this.previewPanel.style.position = 'relative';
        this.previewPanel.appendChild(div);
    }

    private hideLoading(): void {
        if (this.loadingEl) {
            this.loadingEl.remove();
            this.loadingEl = null;
        }
    }

    // -------------------------------------------------------------------------
    // Sandbox
    // -------------------------------------------------------------------------

    setSandbox(sandbox: ArtifactSandbox): void {
        this.sandbox = sandbox;
        this.showLoading();
        sandbox.mount(this.previewPanel, () => {
            this.hideLoading();
            sandbox.reveal();
        });
    }

    getElement(): HTMLDivElement {
        return this.container;
    }

    destroy(): void {
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.sandbox?.destroy();
    }

    // -------------------------------------------------------------------------
    // Helpers
    // -------------------------------------------------------------------------

    private escapeHtml(str: string): string {
        return str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }
}
